import type { PricingWaterfall } from './types';

export interface PickupWaterfallReconciliation {
  basePrice: number | null;
  knownAdjustmentPoints: number;
  reconciliationPoints: number | null;
  netPrice: number | null;
  reconciles: boolean;
}

function finiteNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function sum(values: unknown[]): number {
  return values.reduce<number>((total, value) => total + (finiteNumber(value) || 0), 0);
}

export function buildPickupWaterfallReconciliation(
  waterfall: PricingWaterfall,
  netPrice: number | null
): PickupWaterfallReconciliation {
  const basePrice = finiteNumber(waterfall.pricingDetail?.parPrice);
  const normalizedNetPrice = finiteNumber(netPrice);
  const knownAdjustmentPoints = sum([
    ...waterfall.adjustments
      .filter((adjustment) => adjustment.adjustmentType === 'Price')
      .map((adjustment) => adjustment.adjustor),
    ...waterfall.investorAdjustments
      .filter((adjustment) => adjustment.adjustmentType === 'Price')
      .map((adjustment) => adjustment.adjustor),
    ...waterfall.markups.map((markup) => markup.netMarkupPercent)
  ]);
  if (basePrice === null || normalizedNetPrice === null) {
    return {
      basePrice,
      knownAdjustmentPoints,
      reconciliationPoints: null,
      netPrice: normalizedNetPrice,
      reconciles: false
    };
  }
  const unexplained = normalizedNetPrice - (basePrice + knownAdjustmentPoints);
  const reconciliationPoints = Math.abs(unexplained) < 0.0005 ? 0 : unexplained;
  const calculatedNet = basePrice + knownAdjustmentPoints + reconciliationPoints;
  return {
    basePrice,
    knownAdjustmentPoints,
    reconciliationPoints,
    netPrice: normalizedNetPrice,
    reconciles: Math.abs(calculatedNet - normalizedNetPrice) < 0.0005
  };
}
